import { CheckCircleFilled } from "@ant-design/icons"
import { Empty } from "antd"
import dayjs from "dayjs"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { CardComponent } from "../../../components/Card/CardComponent.tsx"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { cn } from "../../../utils/cn.ts"
import { PortalPageContainer } from "../components/PortalPageContainer.tsx"
import { formatPortalDate } from "../helpers/format-date.ts"
import type { PortalProjectPhase } from "../types/portal.types.ts"

interface TimelineRow {
  phase: PortalProjectPhase
  start: dayjs.Dayjs | null
  end: dayjs.Dayjs | null
  isConfirmed: boolean
}

function toRow(phase: PortalProjectPhase): TimelineRow {
  const start = phase.start_date ? dayjs(phase.start_date) : null
  const end = phase.actual_end_date
    ? dayjs(phase.actual_end_date)
    : phase.end_date
      ? dayjs(phase.end_date)
      : null
  return { phase, start, end, isConfirmed: Boolean(phase.actual_end_date) }
}

/** A read-only, customer-facing rendering of the same milestone data the
 * GC app's Gantt reads (there is no separate Gantt-only endpoint — the
 * staff Gantt view computes its bars from this identical list client-side
 * too) — grouped by project type, with a bar's fill distinguishing a
 * confirmed date (actual_end_date set) from a still-estimated one. */
export function ProjectTimelineTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation()
  const isMobile = useAppSelector((state) => state.nav.isMobile)
  const detail = useAppSelector((state) => state.portal.projectDetail)

  const groups = useMemo(() => {
    const types = detail?.project_type_details ?? []
    const phases = detail?.project_phases ?? []
    const byType = types.map((type) => ({
      type,
      rows: phases.filter((phase) => phase.project_type === type.id).map(toRow)
    }))
    const ungrouped = phases
      .filter((phase) => !types.some((type) => type.id === phase.project_type))
      .map(toRow)
    if (ungrouped.length > 0) {
      byType.push({ type: { id: "none", name: t("portal.project.otherMilestones") }, rows: ungrouped })
    }
    return byType.filter((group) => group.rows.length > 0)
  }, [detail?.project_type_details, detail?.project_phases, t])

  const range = useMemo(() => {
    const allRows = groups.flatMap((group) => group.rows)
    const dates = allRows.flatMap((row) => [row.start, row.end]).filter((d): d is dayjs.Dayjs => Boolean(d))
    const today = dayjs()
    if (dates.length === 0) return { min: today.subtract(7, "day"), max: today.add(7, "day") }
    let min = dates[0]
    let max = dates[0]
    dates.forEach((d) => {
      if (d.isBefore(min)) min = d
      if (d.isAfter(max)) max = d
    })
    // Pad both ends a little so a bar at the very edge, and the "today"
    // line when it falls outside the milestone range entirely, both stay
    // visible rather than pinned flush to the container edge.
    return { min: min.subtract(3, "day"), max: max.add(3, "day") }
  }, [groups])

  const totalDays = Math.max(1, range.max.diff(range.min, "day"))
  const todayPercent = Math.min(
    100,
    Math.max(0, (dayjs().diff(range.min, "day") / totalDays) * 100)
  )

  if (!detail || detail.id !== projectId) return null

  if (groups.length === 0) {
    return (
      <PortalPageContainer className="max-w-4xl">
        <CardComponent>
          <Empty description={t("portal.project.noMilestonesYet")} />
        </CardComponent>
      </PortalPageContainer>
    )
  }

  // jsdom cannot see layout, and a hand-drawn Gantt bar is exactly the kind
  // of thing that only really proves itself in a real browser — the mobile
  // branch below is a plain ordered list precisely so there's always a
  // fully-readable alternative that needs no positioning math at all.
  if (isMobile) {
    return (
      <PortalPageContainer className="flex max-w-3xl flex-col gap-4">
        {groups.map((group) => (
          <CardComponent key={group.type.id} title={group.type.name} size="small">
            <ul>
              {group.rows.map(({ phase, start, end, isConfirmed }) => (
                <li key={phase.id} className="border-b border-gray-50 py-2.5 last:border-0">
                  <p className="text-sm font-medium text-gray-800">{phase.name}</p>
                  <p className="text-xs text-gray-500">
                    {start ? formatPortalDate(start.toISOString()) : "—"}
                    {" – "}
                    {end ? formatPortalDate(end.toISOString()) : t("portal.project.timelineNoDate")}
                    {end && (
                      <span className={cn("ml-1.5", isConfirmed ? "text-emerald-600" : "text-amber-600")}>
                        {isConfirmed
                          ? t("portal.project.timelineConfirmed")
                          : t("portal.project.timelineEstimated")}
                      </span>
                    )}
                  </p>
                </li>
              ))}
            </ul>
          </CardComponent>
        ))}
      </PortalPageContainer>
    )
  }

  return (
    <PortalPageContainer className="flex max-w-5xl flex-col gap-4">
      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-4 rounded-full bg-emerald-500" />
          {t("portal.project.timelineConfirmed")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-4 rounded-full bg-amber-400" />
          {t("portal.project.timelineEstimated")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-0.5 bg-red-500" />
          {t("portal.project.timelineToday")}
        </span>
      </div>

      {groups.map((group) => (
        <CardComponent key={group.type.id} title={group.type.name} size="small">
          <div className="relative">
            <div
              className="pointer-events-none absolute inset-y-0 z-10 w-0.5 bg-red-500"
              style={{ left: `${todayPercent}%` }}
              aria-hidden
            />
            <ul className="flex flex-col gap-3">
              {group.rows.map(({ phase, start, end, isConfirmed }) => {
                const left = start ? Math.max(0, (start.diff(range.min, "day") / totalDays) * 100) : 0
                const right = end ? Math.max(0, (end.diff(range.min, "day") / totalDays) * 100) : left
                const width = Math.max(1.5, right - left)
                return (
                  <li key={phase.id} className="flex items-center gap-3">
                    <div className="w-36 shrink-0 truncate text-sm text-gray-700" title={phase.name}>
                      {phase.name}
                    </div>
                    <div className="relative h-5 flex-1 rounded bg-gray-50">
                      <div
                        className={cn(
                          "absolute inset-y-0 flex items-center rounded px-1.5 text-[10px] font-medium text-white",
                          isConfirmed ? "bg-emerald-500" : "bg-amber-400"
                        )}
                        style={{ left: `${left}%`, width: `${width}%` }}
                      >
                        {isConfirmed && <CheckCircleFilled className="shrink-0" />}
                      </div>
                    </div>
                    <div className="w-24 shrink-0 text-right text-xs text-gray-400">
                      {end ? formatPortalDate(end.toISOString()) : "—"}
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>
        </CardComponent>
      ))}
    </PortalPageContainer>
  )
}
